/**
 * Design Tokens
 * Single source of truth for all design values across the application
 *
 * Usage:
 * import { designTokens } from '@/theme/designTokens';
 * <div className={designTokens.spacing.section}>
 */

export const designTokens = {
  // ==========================================
  // SPACING
  // ==========================================
  spacing: {
    // Individual spacing units
    xs: '0.25rem',    // 4px
    sm: '0.5rem',     // 8px
    md: '1rem',       // 16px
    lg: '1.5rem',     // 24px
    xl: '2rem',       // 32px
    '2xl': '2.5rem',  // 40px
    '3xl': '3rem',    // 48px

    // Common patterns (Tailwind classes)
    section: 'mb-6',           // Between major sections
    subsection: 'mb-4',        // Between subsections
    element: 'mb-2',           // Between related elements
    inline: 'gap-2',           // Between inline items
    inlineLarge: 'gap-4',      // Between larger inline items

    // Page padding
    pageX: 'px-6',             // Horizontal page padding
    pageY: 'py-6',             // Vertical page padding
    page: 'px-6 py-6',         // Full page padding

    // Container padding
    cardPadding: 'p-5',        // Standard card padding
    cardPaddingCompact: 'p-3', // Compact card padding
    cardPaddingLarge: 'p-6',   // Large card padding

    // Header padding
    headerPadding: 'px-6 py-4',
    headerPaddingLarge: 'px-6 py-6',
  },

  // ==========================================
  // TYPOGRAPHY
  // ==========================================
  typography: {
    // Page titles
    h1: {
      className: 'text-3xl font-bold text-gray-900',
      size: 'text-3xl',
      weight: 'font-bold',
      color: 'text-gray-900',
    },

    // Section headers
    h2: {
      className: 'text-xl font-semibold text-gray-900',
      size: 'text-xl',
      weight: 'font-semibold',
      color: 'text-gray-900',
    },

    // Subsection headers
    h3: {
      className: 'text-lg font-semibold text-gray-900',
      size: 'text-lg',
      weight: 'font-semibold',
      color: 'text-gray-900',
    },

    // Card headers
    h4: {
      className: 'text-sm font-semibold text-gray-900',
      size: 'text-sm',
      weight: 'font-semibold',
      color: 'text-gray-900',
    },

    // Form labels, emphasized text
    label: {
      className: 'text-sm font-medium text-gray-700',
      size: 'text-sm',
      weight: 'font-medium',
      color: 'text-gray-700',
    },

    // Body text
    body: {
      className: 'text-base font-normal text-gray-900',
      size: 'text-base',
      weight: 'font-normal',
      color: 'text-gray-900',
    },

    // Body text - secondary
    bodySecondary: {
      className: 'text-base font-normal text-gray-600',
      size: 'text-base',
      weight: 'font-normal',
      color: 'text-gray-600',
    },

    // Small text (meta info, descriptions)
    small: {
      className: 'text-sm font-normal text-gray-600',
      size: 'text-sm',
      weight: 'font-normal',
      color: 'text-gray-600',
    },

    // Extra small text (timestamps, helper text)
    xs: {
      className: 'text-xs font-normal text-gray-500',
      size: 'text-xs',
      weight: 'font-normal',
      color: 'text-gray-500',
    },

    // Helper/meta text
    helper: {
      className: 'text-xs text-gray-500',
      size: 'text-xs',
      color: 'text-gray-500',
    },
  },

  // ==========================================
  // COLORS
  // ==========================================
  colors: {
    // Primary brand color
    primary: {
      50: 'bg-blue-50',
      100: 'bg-blue-100',
      600: 'bg-blue-600',
      700: 'bg-blue-700',
      text: 'text-blue-600',
      textHover: 'text-blue-700',
      border: 'border-blue-200',
    },

    // Status colors
    status: {
      success: {
        bg: 'bg-emerald-50',
        text: 'text-emerald-700',
        border: 'border-emerald-200',
        icon: 'text-emerald-600',
      },
      warning: {
        bg: 'bg-amber-50',
        text: 'text-amber-700',
        border: 'border-amber-200',
        icon: 'text-amber-600',
      },
      danger: {
        bg: 'bg-red-50',
        text: 'text-red-700',
        border: 'border-red-200',
        icon: 'text-red-600',
      },
      info: {
        bg: 'bg-blue-50',
        text: 'text-blue-700',
        border: 'border-blue-200',
        icon: 'text-blue-600',
      },
    },

    // Neutral grays
    gray: {
      50: 'bg-gray-50',
      100: 'bg-gray-100',
      200: 'bg-gray-200',
      300: 'bg-gray-300',
      400: 'text-gray-400',
      500: 'text-gray-500',
      600: 'text-gray-600',
      700: 'text-gray-700',
      900: 'text-gray-900',
    },
  },

  // ==========================================
  // BORDERS & SHADOWS
  // ==========================================
  borders: {
    default: 'border border-gray-200',
    light: 'border border-gray-100',
    none: 'border-0',
  },

  shadows: {
    none: '',
    sm: 'shadow-sm',
    default: 'shadow',
    md: 'shadow-md',
    lg: 'shadow-lg',
  },

  rounded: {
    none: 'rounded-none',
    sm: 'rounded-sm',
    default: 'rounded',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    full: 'rounded-full',
  },

  // ==========================================
  // BUTTON STYLES (as className strings)
  // ==========================================
  buttons: {
    base: 'inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',

    primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
    secondary: 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-blue-500',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
    success: 'bg-emerald-600 text-white hover:bg-emerald-700 focus:ring-emerald-500',
    ghost: 'text-gray-700 hover:bg-gray-100 focus:ring-blue-500',
    link: 'text-blue-600 hover:text-blue-700 hover:underline focus:ring-blue-500',

    // Size variants
    sizes: {
      sm: 'px-3 py-1.5 text-xs',
      md: 'px-4 py-2 text-sm',
      lg: 'px-5 py-2.5 text-base',
    },
  },

  // ==========================================
  // BADGE STYLES (Status indicators)
  // ==========================================
  badges: {
    base: 'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium border',

    // Status-specific styles
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    inactive: 'bg-gray-50 text-gray-600 border-gray-200',
    prospective: 'bg-amber-50 text-amber-700 border-amber-200',
    prospect: 'bg-amber-50 text-amber-700 border-amber-200',

    // Project statuses
    planning: 'bg-purple-50 text-purple-700 border-purple-200',
    in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
    review: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    on_hold: 'bg-amber-50 text-amber-700 border-amber-200',
    cancelled: 'bg-red-50 text-red-700 border-red-200',

    // Task statuses
    todo: 'bg-gray-50 text-gray-700 border-gray-200',
    done: 'bg-emerald-50 text-emerald-700 border-emerald-200',

    // Priority badges
    high: 'bg-red-50 text-red-700 border-red-200',
    medium: 'bg-amber-50 text-amber-700 border-amber-200',
    low: 'bg-blue-50 text-blue-700 border-blue-200',
  },

  // ==========================================
  // CARD STYLES
  // ==========================================
  cards: {
    base: 'bg-white border border-gray-200 rounded-lg',
    default: 'bg-white border border-gray-200 rounded-lg p-5',
    compact: 'bg-white border border-gray-200 rounded-lg p-3',
    large: 'bg-white border border-gray-200 rounded-lg p-6',
    hover: 'bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md hover:border-blue-300 transition-all cursor-pointer',

    // Alternative backgrounds
    gray: 'bg-gray-50 border border-gray-100 rounded-lg p-5',
    grayCompact: 'bg-gray-50 border border-gray-100 rounded-lg p-3',
  },

  // ==========================================
  // FORM STYLES
  // ==========================================
  forms: {
    label: 'block text-sm font-medium text-gray-700 mb-2',
    labelRequired: 'block text-sm font-medium text-gray-700 mb-2 after:content-["*"] after:ml-0.5 after:text-red-500',
    input: 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all disabled:bg-gray-50 disabled:cursor-not-allowed',
    inputError: 'w-full px-3 py-2 border border-red-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all',
    textarea: 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none',
    select: 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all',
    error: 'mt-1 text-xs text-red-600',
    helper: 'mt-1 text-xs text-gray-500',
  },

  // ==========================================
  // LAYOUT PATTERNS
  // ==========================================
  layouts: {
    page: 'flex-1 flex flex-col min-h-screen bg-gray-50',
    pageHeader: 'bg-white border-b border-gray-200',
    pageContent: 'flex-1 overflow-auto',

    // Container widths
    containerNarrow: 'max-w-4xl mx-auto',
    containerWide: 'max-w-7xl mx-auto',
    containerFull: 'w-full',

    // Flex layouts
    flexRow: 'flex items-center gap-2',
    flexRowBetween: 'flex items-center justify-between',
    flexCol: 'flex flex-col gap-2',
  },

  // ==========================================
  // TRANSITIONS
  // ==========================================
  transitions: {
    default: 'transition-all duration-200',
    fast: 'transition-all duration-150',
    slow: 'transition-all duration-300',
    colors: 'transition-colors duration-200',
  },
};

// ==========================================
// UTILITY FUNCTIONS
// ==========================================

/**
 * Get status badge classes by status value
 */
export function getStatusBadgeClass(status: string): string {
  const normalizedStatus = status.toLowerCase().replace(/-/g, '_');
  return designTokens.badges[normalizedStatus as keyof typeof designTokens.badges] || designTokens.badges.inactive;
}

/**
 * Get status color for inline usage
 */
export function getStatusColor(status: string): { bg: string; text: string; border: string } {
  const statusMap: Record<string, { bg: string; text: string; border: string }> = {
    active: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
    inactive: { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' },
    prospective: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
    prospect: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
    planning: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
    in_progress: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
    review: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
    completed: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
    on_hold: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
    cancelled: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
    todo: { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' },
    done: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  };

  return statusMap[status.toLowerCase().replace(/-/g, '_')] || statusMap.inactive;
}

/**
 * Format status text (replace underscores with spaces, capitalize)
 */
export function formatStatusText(status: string): string {
  return status
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export default designTokens;
